import { useDeferredValue, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UsersIcon } from '@heroicons/react/24/outline';
import type { UserListItem } from '@avisoma/shared';
import { PageHeader } from '../../components/PageHeader';
import { Input } from '../../catalyst/input';
import { Button } from '../../catalyst/button';
import { Switch, SwitchField } from '../../catalyst/switch';
import { Label } from '../../catalyst/fieldset';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import backend from '../../services/backend';
import { UsersTable } from './UsersTable';
import { CreateUserDialog } from './CreateUserDialog';
import { EditRoleDialog } from './EditRoleDialog';
import { ResetPasswordDialog } from './ResetPasswordDialog';
import { DeleteUserDialog } from './DeleteUserDialog';
import { DeactivateUserDialog } from './DeactivateUserDialog';
import { ReactivateUserDialog } from './ReactivateUserDialog';

export default function Users() {
  useDocumentTitle('Users');
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [includeDeactivated, setIncludeDeactivated] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRoleTarget, setEditRoleTarget] = useState<UserListItem | null>(null);
  const [resetTarget, setResetTarget] = useState<UserListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserListItem | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<UserListItem | null>(null);
  const [reactivateTarget, setReactivateTarget] = useState<UserListItem | null>(null);

  const query = useQuery({
    queryKey: ['users', { includeDeactivated }],
    queryFn: () => backend.listUsers({ includeDeactivated }).then((res) => res.data),
  });

  const filtered = filterUsers(query.data ?? [], deferredSearch);

  return (
    <div>
      <PageHeader
        title="Users"
        description="Application users with their roles and last login."
        icon={<UsersIcon className="size-6" />}
      >
        <div className="flex w-full items-center gap-3 max-w-2xl text-nowrap">
          <Input
            placeholder="Search by username or role…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search users"
          />
          <SwitchField className="shrink-0">
            <Label className="text-sm whitespace-nowrap">Show deactivated</Label>
            <Switch checked={includeDeactivated} onChange={setIncludeDeactivated} aria-label="Show deactivated users" />
          </SwitchField>
          <Button color="blue" onClick={() => setCreateOpen(true)}>
            New user
          </Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4">
        {query.isLoading ? (
          <div className="text-center text-zinc-500 dark:text-zinc-400 py-12">Loading…</div>
        ) : query.isError ? (
          <div className="bg-white dark:bg-zinc-800 shadow rounded-lg p-6">
            <div className="text-center text-red-600 dark:text-red-400">
              {query.error instanceof Error ? query.error.message : 'Failed to load users'}
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-xl font-extralight text-zinc-500 dark:text-zinc-400 py-12">
            No users match your search.
          </div>
        ) : (
          <UsersTable
            users={filtered}
            onEditRole={setEditRoleTarget}
            onResetPassword={setResetTarget}
            onDelete={setDeleteTarget}
            onDeactivate={setDeactivateTarget}
            onReactivate={setReactivateTarget}
          />
        )}
      </div>

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditRoleDialog
        key={editRoleTarget?.id ?? 'edit-role-empty'}
        user={editRoleTarget}
        open={!!editRoleTarget}
        onClose={() => setEditRoleTarget(null)}
      />
      <ResetPasswordDialog user={resetTarget} open={!!resetTarget} onClose={() => setResetTarget(null)} />
      <DeleteUserDialog user={deleteTarget} open={!!deleteTarget} onClose={() => setDeleteTarget(null)} />
      <DeactivateUserDialog
        user={deactivateTarget}
        open={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
      />
      <ReactivateUserDialog
        user={reactivateTarget}
        open={!!reactivateTarget}
        onClose={() => setReactivateTarget(null)}
      />
    </div>
  );
}

function filterUsers(users: UserListItem[], q: string): UserListItem[] {
  const needle = q.trim().toLowerCase();
  if (needle.length === 0) return users;
  return users.filter((u) => u.username.toLowerCase().includes(needle) || u.role.toLowerCase().includes(needle));
}
